import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios"
import { newArchAxiosInstance } from "utils/request"
import { useProcessMonitorStore } from "stores/process-monitor"
import {
  beginTransactionTracking,
  failTransactionTracking,
} from "./transactionProgressClient"
import {
  findTrackedBulkOperation,
  getTrackedBulkDestination,
} from "./processRegistry"
import { getTransactionKey } from "./types"

type ProgressMetadata = {
  type: string
  id: string
}

type TrackedRequestConfig = InternalAxiosRequestConfig & {
  processTracking?: ProgressMetadata
}

const getFileName = (data: unknown) => {
  if (!(data instanceof FormData)) return undefined
  const upload = data.get("file_upload") as File | null
  return upload?.name
}

/**
 * The bulk compensation imports post the same endpoint twice: once to VALIDATE (no `confirm`
 * field, nothing is queued, the response just reports the counts so the user can confirm) and
 * again to actually run it. Only the second one is a background process, so the first must not
 * be tracked - otherwise the monitor shows a task that will never progress or complete.
 *
 * Only consulted for operations marked `twoPhase`. Nearly every upload in the app posts a
 * `file_upload` field and none of the others send `confirm`, so applying this to all of them
 * would silently stop tracking every existing bulk upload.
 */
const isUnconfirmedDryRun = (data: unknown) =>
  data instanceof FormData && data.has("file_upload") && !data.has("confirm")

const attachInterceptors = (instance: AxiosInstance) => {
  const requestInterceptor = instance.interceptors.request.use(
    async (config: TrackedRequestConfig) => {
      if (config.processTracking) return config

      const operation = findTrackedBulkOperation(config.url, config.method)
      if (!operation) return config
      if (operation.twoPhase && isUnconfirmedDryRun(config.data)) return config

      const id = operation.type
      const fileName = getFileName(config.data)
      const currentPath = `${window.location.pathname}${window.location.search}`

      await beginTransactionTracking({
        type: operation.type,
        id,
        title: fileName ? `${operation.title}: ${fileName}` : operation.title,
        message: "Starting upload",
        destinationPath: getTrackedBulkDestination(operation, currentPath),
      })

      config.processTracking = { type: operation.type, id }
      useProcessMonitorStore
        .getState()
        .setFocusedProcess(getTransactionKey(operation.type, id))

      return config
    }
  )

  const responseInterceptor = instance.interceptors.response.use(
    (response) => {
      const tracking = (response.config as TrackedRequestConfig).processTracking
      const responseData = response.data

      if (
        tracking &&
        (responseData?.success === false || responseData?.status === "error")
      ) {
        failTransactionTracking(
          tracking.type,
          tracking.id,
          responseData?.message || responseData?.msg || "Upload failed"
        )
      }

      return response
    },
    (error: AxiosError) => {
      const tracking = (error.config as TrackedRequestConfig | undefined)
        ?.processTracking

      if (tracking) {
        const responseData = error.response?.data as
          | { message?: string; msg?: string }
          | undefined
        failTransactionTracking(
          tracking.type,
          tracking.id,
          responseData?.message || responseData?.msg || "Upload failed"
        )
      }

      return Promise.reject(error)
    }
  )

  return () => {
    instance.interceptors.request.eject(requestInterceptor)
    instance.interceptors.response.eject(responseInterceptor)
  }
}

export const installBulkProgressInterceptors = () => {
  const ejectors = [newArchAxiosInstance].map(attachInterceptors)
  return () => ejectors.forEach((eject) => eject())
}
